import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import AdminBusinessRegistration from '../../components/admin/AdminBusinessRegistration';
import Toast from '../../components/common/Toast';

/**
 * AdminBusinessesPage
 * Main admin page for managing business registrations
 * Features: List view, toolbar (search/filter/button), pagination, drawer forms
 */
const AdminBusinessesPage = () => {
  const navigate = useNavigate();

  // State
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    createdBy: 'all', // all, owner, admin
    verified: 'all', // all, verified, unverified
    published: 'all', // all, published, unpublished
  });

  // Sorting & Pagination
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // UI State
  const [showForm, setShowForm] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  /**
   * Fetch categories from API
   */
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await fetch(
        'https://vmc-backend-vt2w.onrender.com/api/categories',
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Silently fail - categories are optional
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  /**
   * Fetch businesses from API
   */
  const fetchBusinesses = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('perpage', itemsPerPage);

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      if (filters.createdBy !== 'all') {
        params.append('created_by_type', filters.createdBy);
      }

      if (filters.verified !== 'all') {
        params.append('is_verified', filters.verified === 'verified');
      }

      if (filters.published !== 'all') {
        params.append('is_published', filters.published === 'published');
      }

      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await fetch(
        `https://vmc-backend-vt2w.onrender.com/api/admin/businesses?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }

      const data = await response.json();
      setBusinesses(data.data || []);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      showToast('Failed to load businesses', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchCategories(); // Fetch categories on mount
    fetchBusinesses();
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchQuery, filters, sortBy, sortOrder]);

  // Refetch when page changes
  useEffect(() => {
    fetchBusinesses();
  }, [currentPage]);

  /**
   * Handle create business
   */
  const handleCreate = () => {
    setSelectedBusiness(null);
    setShowForm(true);
  };

  /**
   * Handle edit business
   */
  const handleEdit = (business) => {
    setSelectedBusiness(business);
    setShowForm(true);
  };

  /**
   * Handle delete business
   */
  const handleDelete = (business) => {
    setBusinessToDelete(business);
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete
   */
  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);

      const response = await fetch(
        `https://vmc-backend-vt2w.onrender.com/api/admin/businesses/${businessToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete business');
      }

      showToast('Business deleted successfully', 'success');
      setShowDeleteModal(false);
      setBusinessToDelete(null);
      fetchBusinesses();
    } catch (error) {
      console.error('Error deleting business:', error);
      showToast('Failed to delete business', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      const endpoint = selectedBusiness
        ? `https://vmc-backend-vt2w.onrender.com/api/admin/businesses/${selectedBusiness.id}`
        : 'https://vmc-backend-vt2w.onrender.com/api/admin/businesses';

      const method = selectedBusiness ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save business');
      }

      setShowForm(false);
      setSelectedBusiness(null);
      fetchBusinesses();
      setCurrentPage(1);
    } catch (error) {
      console.error('Error saving business:', error);
      // Re-throw so the child component can catch and display
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Get category name by UUID
   */
  const getCategoryName = (categoryId) => {
    if (!categoryId) return '—';
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : '—';
  };

  /**
   * Format date safely
   */
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  /**
   * Show toast notification
   */
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  /**
   * Calculate stats
   */
  const stats = {
    total: businesses.length || 0,
    verified: businesses.filter((b) => b.is_verified).length || 0,
    published: businesses.filter((b) => b.is_published).length || 0,
  };

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Businesses' },
  ];

  // Skeleton loader
  const SkeletonRow = () => (
    <tr className="border-b border-slate-200 hover:bg-slate-50">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-slate-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );

  const StarRating = ({ value }) => {
    if (!value) return <span className="text-[11px] text-slate-400">No ratings</span>;
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-amber-400">★</span>
        <span className="text-[12px] font-semibold text-slate-700">{parseFloat(value).toFixed(1)}</span>
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* ── Breadcrumb & Page Title ── */}
        <div>
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-[24px] font-bold text-slate-900 mt-3">Manage Businesses</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Create and manage business registrations in your network
          </p>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Total Businesses
            </p>
            <p className="text-[28px] font-bold text-slate-900 mt-2">{stats.total}</p>
          </div>

          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Verified
            </p>
            <p className="text-[28px] font-bold text-green-600 mt-2">{stats.verified}</p>
          </div>

          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Published
            </p>
            <p className="text-[28px] font-bold text-blue-600 mt-2">{stats.published}</p>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-col gap-3">
          {/* Search & Filter Row */}
          <div className="flex gap-3 items-center flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* New Business Button */}
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-[12px] font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <span>+</span> New Business
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {/* Created By */}
            <select
              value={filters.createdBy}
              onChange={(e) =>
                setFilters({ ...filters, createdBy: e.target.value })
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Created By: All</option>
              <option value="owner">Created By: Owner</option>
              <option value="admin">Created By: Admin</option>
            </select>

            {/* Verified Status */}
            <select
              value={filters.verified}
              onChange={(e) =>
                setFilters({ ...filters, verified: e.target.value })
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Verified: All</option>
              <option value="verified">Verified: Yes</option>
              <option value="unverified">Verified: No</option>
            </select>

            {/* Published Status */}
            <select
              value={filters.published}
              onChange={(e) =>
                setFilters({ ...filters, published: e.target.value })
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Published: All</option>
              <option value="published">Published: Yes</option>
              <option value="unpublished">Published: No</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="business_name">Sort: Name</option>
              <option value="created_at">Sort: Created Date</option>
              <option value="updated_at">Sort: Updated Date</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() =>
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              title={`Toggle sort order`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="overflow-hidden border border-slate-200 rounded-lg bg-white">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Business Name
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-[12px] font-semibold text-slate-700">
                  Reviews
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Created
                </th>
                {/* <th className="px-4 py-3 text-right text-[12px] font-semibold text-slate-700">
                  Actions
                </th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : businesses.length > 0 ? (
                businesses.map((business) => (
                  <tr
                    key={business.id}
                    onClick={() => navigate(`/admin/businesses/${business.id}`)}
                    className="border-b border-slate-200 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-[13px] font-medium text-slate-900">
                      {business.business_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {getCategoryName(business.business_category)}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {business.phone_number || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] space-y-1">
                      <div className="flex gap-1 flex-wrap">
                        {business.is_verified && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full">
                            ✓ Verified
                          </span>
                        )}
                        {business.is_published && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full">
                            ✓ Published
                          </span>
                        )}
                        {!business.is_verified && !business.is_published && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full">
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        to={`/admin/businesses/${business.id}/reviews`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex flex-col items-center gap-0.5 group"
                        title="View reviews"
                      >
                        <span className="text-[13px] font-semibold text-blue-600 group-hover:text-blue-700 transition-colors">
                          {business.total_reviews ?? 0}
                        </span>
                        {business.avg_rating ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-500 font-medium">
                            ★ {parseFloat(business.avg_rating).toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">no reviews</span>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {formatDate(business.created_at)}
                    </td>
                    {/* <td className="px-4 py-3 text-right space-x-1.5">
                      <button
                        onClick={() => handleEdit(business)}
                        className="inline-flex px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(business)}
                        className="inline-flex px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <p className="text-[13px] text-slate-500">
                      No businesses found. Create your first registration.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {businesses.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-slate-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, stats.total + itemsPerPage)}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={businesses.length < itemsPerPage || loading}
                className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Right Drawer Form ── */}
      <AdminBusinessRegistration
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedBusiness(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedBusiness}
        isLoading={isSubmitting}
        categories={categories}
        title={selectedBusiness ? 'Edit Business' : 'Register Business'}
      />

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && businessToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-[400px] w-full space-y-4 p-6">
            <div>
              <h3 className="text-[15px] font-bold text-slate-900">Delete Business?</h3>
              <p className="text-[13px] text-slate-600 mt-1">
                Are you sure you want to delete{' '}
                <strong>{businessToDelete.business_name}</strong>? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setBusinessToDelete(null);
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-[12px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast Notifications ── */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminBusinessesPage;
